import _ from 'underscore';
import Promise from 'bluebird';
import { showRejectionReasons } from './rejection/show-rejection-reasons';

/**
 * Manages creation of delivery status column popups
 * This popup is based on the projection grid
 */
export class DeliveryStatusPopupFactory {
  /**
   * Constructs the factory, associating with a grid and rejection view handler
   * @param {Object} grid - The grid to which this factory will be associated
   * @param {Object} i18n - Localization function
   */
  constructor({
    grid,
    i18n,
  }) {
    this.grid = grid;
    this.i18n = i18n;
  }

  /**
    * Creates entity object from event target using the grid associated with the factory.
    *
    * @param {Object} event - The click event from where the quality score cell was clicked.
    * @returns {Object} entity - The entity object for the clicked row
    *  it should contains at least { Name, DeliveryStatusDetails, Id } as property.
  */
  getEventTargetEntity(event) {
    const model = this.getEventTargetModel(event);

    return model;
  }

  /**
    * Gets model object from event target using the grid associated with the factory.
    *
    * @param {Object} event - The click event from where the quality score cell was clicked.
    * @returns {Object} entity - The entity object for the clicked row
    *  it should contains at least { Name, DeliveryStatusDetails, Id } as property.
  */
  getEventTargetModel(event) {
    const key = this.grid.gridView.keyOfElement(event.target);

    return this.grid.gridView.itemWithKey(key);
  }

  /**
    * show campaign reject reasons
    *
    * @param {Object} event - The click event from where the quality score cell was clicked.
    * @param {Function} [getCustomeTemplate] - Method that returns whether to use custome template
    * @param {Function} [useCustomTemplate] - Methot that returns custom reason templates
    * @param {String} [containerSelector] - popup container selector
    * @param {String} [popoverId] - popup id
    * @param {Object} [view] -  view that has event triggered to close the popup
    * @returns {undefined}.
  */
  showRejectionPopup({
    event,
    useCustomTemplate,
    getCustomTemplate,
    actionLinks,
    containerSelector,
    isContextualAttributeForRecommendation,
    isInlineDiagnosisForKeyword,
    IsExperimentsOpenBetaEnabled,
    isSmartShoppingCampaignEnabled,
    percentSymbol,
    getInlineDiagnosisForKeywordComponent,
    popoverId,
    view,
    afterRender,
    config,
    instrumentation,
    inContextRepairRecommedationType,
    isEditorialCategoryEnabled,
  }) {
    const entity = this.getEventTargetEntity(event);

    if (entity === null && !isContextualAttributeForRecommendation) {
      return Promise.resolve(false);
    }

    if (IsExperimentsOpenBetaEnabled && entity.ExperimentId && entity.AssociatedExperiment) {
      entity.statusForCombinedPopup = [
        'CampaignUserPaused',
        'InvalidFinancialStatusOther',
        'CampaignSuspended',
        'AutoBiddingLimitedByNotEnoughConversions',
      ];

      if (_.contains(['Promoted', 'Graduated', 'Ended'], entity.AssociatedExperiment.Status)) {
        // Show experiment info only
        entity.DeliveryStatusDetails = ['ExperimentInfo'];
      } else if (!_.contains(entity.statusForCombinedPopup, entity.DeliveryStatus)) {
        // Show original info and experiment info in different pages
        entity.DeliveryStatusDetails = _.union(entity.DeliveryStatusDetails, ['ExperimentInfo']);
      }
      // Otherwise show original info combined with experiment info within the same page
    }

    return showRejectionReasons({
      event,
      entity,
      getCustomTemplate,
      useCustomTemplate,
      deliveryStatusDetails: entity && entity.DeliveryStatusDetails,
      actionLinks,
      i18n: this.i18n,
      getInlineDiagnosisForKeywordComponent,
      isInlineDiagnosisForKeyword,
      isContextualAttributeForRecommendation,
      isSmartShoppingCampaignEnabled,
      containerSelector,
      percentSymbol,
      popoverId,
      view,
      afterRender,
      config,
      instrumentation,
      inContextRepairRecommedationType,
      isForAdsPageOnly: this.grid.config && this.grid.config.name === 'summary_tab_ad',
      isEditorialCategoryEnabled,
    });
  }
}

